import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { leadsApi } from '../api'
import { Lead, STAGE_LABELS, STAGE_COLORS, PRIORITY_COLORS } from '../types'
import LeadForm from './LeadForm'

export default function Leads() {
  const [leads, setLeads] = useState<Lead[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [stageFilter, setStageFilter] = useState('')
  const [showAddModal, setShowAddModal] = useState(false)

  useEffect(() => {
    loadLeads()
  }, [stageFilter])

  async function loadLeads() {
    try {
      setLoading(true)
      const params: any = {}
      if (stageFilter) params.stage = stageFilter
      if (search) params.search = search
      const data = await leadsApi.list(params)
      setLeads(data)
    } catch (error) {
      console.error('Failed to load leads:', error)
    } finally {
      setLoading(false)
    }
  }

  async function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    await loadLeads()
  }

  async function handleDelete(id: number) {
    if (!confirm('Are you sure you want to delete this lead?')) return
    try {
      await leadsApi.delete(id)
      setLeads(leads.filter(l => l.id !== id))
    } catch (error) {
      console.error('Failed to delete lead:', error)
    }
  }

  return (
    <div className="pb-20 lg:pb-0">
      <div className="flex justify-between items-center mb-4 lg:mb-6">
        <h1 className="text-xl lg:text-2xl font-bold">Leads</h1>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-primary text-sm lg:text-base"
        >
          + Add Lead
        </button>
      </div>

      {/* Filters */}
      <div className="card mb-4 lg:mb-6">
        <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Search leads..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input-field flex-1"
          />
          <select
            value={stageFilter}
            onChange={(e) => setStageFilter(e.target.value)}
            className="input-field sm:w-48"
          >
            <option value="">All Stages</option>
            {Object.entries(STAGE_LABELS).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
          <button type="submit" className="btn-primary">
            Search
          </button>
        </form>
      </div>

      {/* Leads List */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="text-center py-8">Loading...</div>
        ) : leads.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            No leads found. Add your first lead to get started!
          </div>
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden lg:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Coaching Center
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Owner
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Phone
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Stage
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Next Follow-up
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {leads.map((lead) => (
                    <tr key={lead.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4">
                        <Link to={`/leads/${lead.id}`} className="font-medium text-primary-600 hover:text-primary-700">
                          {lead.coaching_name}
                        </Link>
                      </td>
                      <td className="px-6 py-4 text-gray-600">{lead.owner_name}</td>
                      <td className="px-6 py-4 text-gray-600">{lead.phone}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${STAGE_COLORS[lead.stage]}`}>
                          {STAGE_LABELS[lead.stage]}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[lead.priority]}`}>
                          {lead.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-600">
                        {lead.next_followup 
                          ? new Date(lead.next_followup).toLocaleDateString()
                          : '-'
                        }
                      </td>
                      <td className="px-6 py-4 text-right space-x-2">
                        <Link to={`/leads/${lead.id}`} className="text-primary-600 hover:text-primary-700">
                          View
                        </Link>
                        <button
                          onClick={() => handleDelete(lead.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile Cards */}
            <div className="lg:hidden space-y-3">
              {leads.map((lead) => (
                <div key={lead.id} className="bg-gray-50 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <Link to={`/leads/${lead.id}`} className="font-semibold text-primary-600 hover:text-primary-700">
                      {lead.coaching_name}
                    </Link>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${PRIORITY_COLORS[lead.priority]}`}>
                      {lead.priority}
                    </span>
                  </div>
                  <p className="text-sm text-gray-600 mb-2">{lead.owner_name}</p>
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${STAGE_COLORS[lead.stage]}`}>
                      {STAGE_LABELS[lead.stage]}
                    </span>
                    {lead.next_followup && (
                      <span className="text-xs text-orange-600">
                        📅 {new Date(lead.next_followup).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="flex justify-between items-center mt-3 pt-3 border-t border-gray-200">
                    <a href={`tel:${lead.phone}`} className="text-sm text-primary-600">
                      📞 {lead.phone}
                    </a>
                    <div className="flex gap-3">
                      <Link to={`/leads/${lead.id}`} className="text-sm text-primary-600">
                        View
                      </Link>
                      <button
                        onClick={() => handleDelete(lead.id)}
                        className="text-sm text-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      {/* Add Lead Modal */}
      {showAddModal && (
        <LeadForm
          onClose={() => setShowAddModal(false)}
          onSaved={(lead) => {
            setLeads([lead, ...leads])
            setShowAddModal(false)
          }}
        />
      )}
    </div>
  )
}
